import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

/* ── Types ── */

/** Espelha `UserResponseDto`: o campo de estado chama `isActive`. */
export interface User {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/* Espelha `RoleResponseDto`: nao ha `updatedAt`. */
export interface Role {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  isSystem: boolean;
  createdAt: string;
}

/* ── User Hooks ── */

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
      /* O backend devolve envelope paginado; a tela consome array.
         Extrair `.data` aqui mantem o tipo publico do hook. */
    queryFn: async () =>
      (await apiClient.get<{ data: User[] }>('/users')).data,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; email: string; password: string }) =>
      apiClient.post<User>('/users', payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    /* `UpdateUserDto` aceita apenas name, email e password. Com
       `forbidNonWhitelisted: true`, mandar qualquer outro campo — `active`
       estava na assinatura — responde 400. */
    mutationFn: ({ id, ...payload }: { id: string; name?: string; email?: string }) =>
      apiClient.patch<User>(`/users/${id}`, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/users/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

/* ── Role Hooks ── */

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => apiClient.get<Role[]>('/roles'),
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; permissions: string[] }) =>
      apiClient.post<Role>('/roles', payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['roles'] }),
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; name?: string; permissions?: string[] }) =>
      apiClient.patch<Role>(`/roles/${id}`, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['roles'] }),
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/roles/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['roles'] }),
  });
}
